import type { ChangeEvent } from 'react';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

type ImportDialogProps = {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => void;
  currentModule: string;
  t: (key: string, params?: any) => string;
};

// ----------------------------------------------------------------------

export function ImportDialog({ open, onClose, onImport, currentModule, t }: ImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<boolean>(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv') {
        setFileError(true);
        setFile(null);
        return;
      }
      setFileError(false);
      setFile(selectedFile);
    }
  };

  const handleImport = () => {
    if (file) {
      onImport(file);
      onClose();
    }
  };

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileError(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('IMPORT_MODULE', { module: t(currentModule.toUpperCase()) })}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" mb={2}>
          {t('IMPORT_CSV_DESCRIPTION', { module: t(currentModule.toUpperCase()) })} {'  '}
          <Link href={`/assets/${currentModule}.csv`} underline="hover" download>
            {t('DOWNLOAD_CSV')}
          </Link>
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <InputLabel htmlFor="csv-upload" sx={{ fontWeight: 500 }}>
            {t('CSV_FILE')}
          </InputLabel>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <label htmlFor="csv-upload" style={{ cursor: 'pointer' }}>
            <IconButton component="span" color="primary">
              <Iconify icon="mdi:file-import" />
            </IconButton>
            <Typography variant="body2" component="span">
              {file ? file.name : t('CHOOSE_FILE')}
            </Typography>
          </label>
        </Box>
        {fileError && (
          <Typography variant="body2" my={0.5} color="error">
            {t('ONLY_CSV_FILES_ARE_ALLOWED')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '15px' }}>
        <Button onClick={onClose} variant="outlined">
          {t('CANCEL')}
        </Button>
        <Button onClick={handleImport} variant="contained" disabled={!file || fileError}>
          {t('IMPORT')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
